/**
 * Color Normalization for AIM Variant Picker
 *
 * Non-destructive: hides duplicate color swatches and relabels the visible
 * one to the base color name. Impact's native inputs stay intact.
 *
 * Uses Impact's product:rerender form event. No MutationObserver.
 */

(function () {
  'use strict';

  var LOG = true; // set false once working

  function log() {
    if (LOG) console.log.apply(console, ['[color-norm]'].concat(Array.from(arguments)));
  }

  var MATERIAL_PREFIXES = {
    'Metallic Film': 'Metallic',
    'DayGlo Tissue': 'DayGlo',
    'DayGlo Film': 'DayGlo Film',
    'Technoflek': 'Technoflek',
    'Glimmer': 'Glimmer',
    'Holographic': 'Holographic',
    'CleanFree™': 'CleanFree'
  };

  /* -------------------------------------------------------------------
   * Build normalization map
   * ---------------------------------------------------------------- */
  function buildNormalization(data) {
    var groups = data.materialGroups;
    var mats = Object.keys(groups);
    if (mats.length < 2) return null;

    var colorSets = {};
    for (var mat in groups) {
      colorSets[mat] = new Set(groups[mat]);
    }

    var normMap = {};
    for (var mat in groups) {
      var prefix = MATERIAL_PREFIXES[mat];
      if (!prefix) continue;
      var colors = groups[mat];
      for (var i = 0; i < colors.length; i++) {
        var color = colors[i];
        if (color.indexOf(prefix + ' ') !== 0) continue;
        var remainder = color.slice(prefix.length + 1).trim();
        if (!remainder) continue;
        for (var otherMat in colorSets) {
          if (otherMat !== mat && colorSets[otherMat].has(remainder)) {
            normMap[color] = remainder;
            break;
          }
        }
      }
    }

    if (Object.keys(normMap).length === 0) return null;

    var normalizedPairs = {};
    for (var prefixed in normMap) {
      var base = normMap[prefixed];
      if (!normalizedPairs[base]) normalizedPairs[base] = {};
      for (var mat in groups) {
        if (groups[mat].indexOf(prefixed) !== -1) normalizedPairs[base][mat] = prefixed;
        if (groups[mat].indexOf(base) !== -1 && !normalizedPairs[base][mat]) {
          normalizedPairs[base][mat] = base;
        }
      }
    }

    return { normMap: normMap, normalizedPairs: normalizedPairs };
  }

  /* -------------------------------------------------------------------
   * DOM helpers
   * ---------------------------------------------------------------- */
  function getLabelEl(input) {
    if (!input || !input.id || !input.parentElement) return null;
    try {
      return input.parentElement.querySelector('label[for="' + CSS.escape(input.id) + '"]');
    } catch (e) {
      return null;
    }
  }

  function getColorName(input) {
    var label = getLabelEl(input);
    if (!label) return '';
    var srOnly = label.querySelector('.sr-only');
    if (srOnly) return srOnly.textContent.trim();
    var textSpan = label.querySelector('span');
    if (textSpan) return textSpan.textContent.trim();
    return label.textContent.trim();
  }

  function getOptionInputs(container, position) {
    return Array.from(container.querySelectorAll('input[data-option-position="' + position + '"]'));
  }

  function getCheckedLabel(container, position) {
    var inputs = getOptionInputs(container, position);
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) return getColorName(inputs[i]);
    }
    return null;
  }

  /* -------------------------------------------------------------------
   * Apply normalization to a picker element
   * ---------------------------------------------------------------- */
  function applyToPicker(picker) {
    try {
      var dataEl = picker.querySelector('script[data-color-normalization]');
      if (!dataEl) {
        log('No normalization data found in picker');
        return;
      }

      var data = JSON.parse(dataEl.textContent);
      log('Parsed data:', data.singleMaterial ? 'single-material' : Object.keys(data.materialGroups).join(' + '));

      var fieldsets = picker.querySelectorAll('fieldset.variant-picker__option');
      log('Found', fieldsets.length, 'fieldsets');

      // Single material: hide the material fieldset
      if (data.singleMaterial && fieldsets.length > 0) {
        fieldsets[0].style.display = 'none';
        log('Hidden single-material fieldset');
        return;
      }

      // Multi-material: normalize colors and hide unavailable
      if (!data.singleMaterial && fieldsets.length >= 2) {
        var norm = buildNormalization(data);
        normalizeColors(picker, fieldsets[1], data, norm);
      }
    } catch (e) {
      console.error('[color-norm] Error in applyToPicker:', e);
    }
  }

  /* -------------------------------------------------------------------
   * Normalize colors
   * ---------------------------------------------------------------- */
  function normalizeColors(picker, colorFieldset, data, norm) {
    var selectedMaterial = getCheckedLabel(picker, '1');
    log('Selected material:', selectedMaterial);
    if (!selectedMaterial) return;

    // Get the set of colors that belong to the selected material
    var materialColors = new Set(data.materialGroups[selectedMaterial] || []);

    var colorInputs = getOptionInputs(picker, '2');
    log('Found', colorInputs.length, 'color inputs');

    var inputByColor = {};
    for (var i = 0; i < colorInputs.length; i++) {
      var name = getColorName(colorInputs[i]);
      if (name) inputByColor[name] = colorInputs[i];
    }

    var hiddenCount = 0;
    var relabeledCount = 0;

    // Step 1: Hide ALL colors that don't belong to the selected material
    for (var colorName in inputByColor) {
      var input = inputByColor[colorName];
      var label = getLabelEl(input);
      if (!label) continue;

      if (!materialColors.has(colorName)) {
        // Check if this is a normalized duplicate that maps TO a color in this material
        // (e.g., "Green" when Metallic is selected — it maps to "Metallic Green")
        var isNormBase = norm && norm.normalizedPairs[colorName] && norm.normalizedPairs[colorName][selectedMaterial];
        if (!isNormBase) {
          input.style.display = 'none';
          label.style.display = 'none';
          hiddenCount++;
          log('  Hidden (not in material):', colorName);
        }
      }
    }

    // Step 2: Handle normalized pairs — hide duplicates, relabel survivors
    if (norm) {
      for (var baseColor in norm.normalizedPairs) {
        var materialMap = norm.normalizedPairs[baseColor];
        var visibleColor = materialMap[selectedMaterial];

        for (var mat in materialMap) {
          var originalColor = materialMap[mat];
          var input2 = inputByColor[originalColor];
          if (!input2) continue;
          var label2 = getLabelEl(input2);
          if (!label2) continue;

          if (originalColor === visibleColor) {
            // Visible — relabel if needed
            if (originalColor !== baseColor) {
              var srOnly = label2.querySelector('.sr-only');
              if (srOnly) srOnly.textContent = baseColor;
              var textSpan = label2.querySelector('span:not(.block-swatch__color):not(.sr-only)');
              if (textSpan) textSpan.textContent = baseColor;
              if (label2.hasAttribute('data-tooltip')) {
                label2.setAttribute('data-tooltip', baseColor);
              }
              relabeledCount++;
              log('  Relabeled:', originalColor, '->', baseColor);
            }
          } else {
            // Duplicate — hide (may already be hidden from step 1)
            input2.style.display = 'none';
            label2.style.display = 'none';
          }
        }
      }
    }

    log('Summary: hidden=' + hiddenCount + ', relabeled=' + relabeledCount);

    // Fix legend
    var checkedInput = picker.querySelector('input[data-option-position="2"]:checked');
    if (checkedInput) {
      var displayName = getColorName(checkedInput);
      var legendSpan = colorFieldset.querySelector('.variant-picker__option-info .h-stack span:not(.text-subdued):not(legend)');
      if (legendSpan) {
        legendSpan.textContent = displayName;
        log('Updated legend to:', displayName);
      }
    }
  }

  /* -------------------------------------------------------------------
   * Lifecycle
   * ---------------------------------------------------------------- */
  function initAll() {
    log('initAll called');
    var pickers = document.querySelectorAll('variant-picker');
    log('Found', pickers.length, 'variant-picker elements');
    pickers.forEach(function (picker) {
      applyToPicker(picker);
      listenForRerenders(picker);
    });
  }

  function listenForRerenders(picker) {
    var formId = picker.getAttribute('form-id');
    if (!formId) return;

    var form = document.getElementById(formId);
    if (!form) {
      log('Form not found by ID:', formId);
      // Try document.forms
      form = document.forms[formId];
      if (!form) {
        log('Form also not found via document.forms:', formId);
        return;
      }
    }

    if (form._normListening) return;
    form._normListening = true;

    log('Listening for product:rerender on form:', formId);

    form.addEventListener('product:rerender', function () {
      log('product:rerender fired on form:', formId);
      requestAnimationFrame(function () {
        var newPickers = document.querySelectorAll('variant-picker[form-id="' + formId + '"]');
        log('After rerender: found', newPickers.length, 'picker(s)');
        newPickers.forEach(applyToPicker);
      });
    });
  }

  function boot() {
    log('Booting');
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    log('shopify:section:load');
    setTimeout(initAll, 100);
  });
})();
